import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaLock } from 'react-icons/fa';
import { calculateAgeFromDob, convertDateToInputFormat, capitalizeRelationship, normalizeRelationshipForApi, formatRelationshipForApi, findMobileSubgroupDuplicate } from '../../utils/subscriberHelpers';


const MALE_RELATIONS = ['father', 'son', 'brother', 'father_in_law'];
const FEMALE_RELATIONS = ['mother', 'daughter', 'sister', 'mother_in_law'];

function getSelfGender(existingMembers) {
  const self = existingMembers.find(m =>
    (m.relation || m.relationship || '').toLowerCase() === 'self'
  );
  if (!self) return '';
  const g = (self.gender || '').toLowerCase();
  if (g === 'male') return 'Male';
  if (g === 'female') return 'Female';
  return '';
}

function getGenderOptions(relation, selfGender) {
  const rel = (relation || '').toLowerCase();
  if (MALE_RELATIONS.includes(rel)) return ['Male', 'Other'];
  if (FEMALE_RELATIONS.includes(rel)) return ['Female', 'Other'];
  if (rel === 'spouse') {
    if (selfGender === 'Male') return ['Female', 'Other'];
    if (selfGender === 'Female') return ['Male', 'Other'];
    return ['Male', 'Female', 'Other'];
  }
  return ['Male', 'Female', 'Other'];
}

const RELATION_GENDER_MAP = {
  father: 'Male', son: 'Male', brother: 'Male', grandfather: 'Male',
  grandson: 'Male', uncle: 'Male', nephew: 'Male', father_in_law: 'Male',
  son_in_law: 'Male', brother_in_law: 'Male',
  mother: 'Female', daughter: 'Female', sister: 'Female', grandmother: 'Female',
  granddaughter: 'Female', aunt: 'Female', niece: 'Female', mother_in_law: 'Female',
  daughter_in_law: 'Female', sister_in_law: 'Female',
};


const today = new Date().toISOString().split('T')[0];

const EditFamilyMemberModal = ({ mounted, show, member, onClose, onSubmit, existingSingletonRelations,existingMembers = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    dob: '',
    age: '',
    gender: '',
    mobile: '',
    email: ''
  });


  const [errors, setErrors] = useState({});
  const [isRelationshipOverride, setIsRelationshipOverride] = useState(false);

  useEffect(() => {
    if (show && member) {
      const rel = member.relationship || member.relation || '';
      const dobRaw = member.dob || member.date_of_birth || member.dateOfBirth || '';
      const dob = convertDateToInputFormat(dobRaw);
      const calculatedAge = calculateAgeFromDob(dobRaw);
      const age = calculatedAge !== null && calculatedAge !== undefined ? calculatedAge : '';
      const rawGender = (member.gender || '').toString().toLowerCase();
      const genderDisplay = rawGender === 'male' ? 'Male' : rawGender === 'female' ? 'Female' : rawGender === 'other' ? 'Other' : '';
      const relationLabel = formatRelationshipForApi(rel);
      const contactNumber = member.contact_number || member.contact || member.mobile || member.phone || '';

      setFormData({
        name: member.name || '',
        relationship: relationLabel,
        dob: dob || '',
        age: age !== null && age !== undefined ? age : '',
        gender: genderDisplay,
        mobile: contactNumber,
        email: member.email || ''
      });
      setIsRelationshipOverride(false);
      setErrors({});
    }
  }, [show, member]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'dob') {
        const calculatedAge = calculateAgeFromDob(value);
        updated.age = calculatedAge !== null && calculatedAge !== undefined ? calculatedAge : '';
      }
      if (field === 'relationship') {
        const autoGender = RELATION_GENDER_MAP[value];
        if (autoGender) updated.gender = autoGender;
      }
      return updated;
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === '') newErrors.name = 'Name is required';

    if (isRelationshipOverride && !formData.relationship) newErrors.relationship = 'Relationship is required';
    if (!formData.dob) newErrors.dob = 'Date of birth is required';
    else if (formData.age !== '' && Number(formData.age) < 1) newErrors.dob = 'Age must be at least 1 year';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.mobile || formData.mobile.trim() === '') {
      newErrors.mobile = 'Mobile is required';
    } else {
      const cleaned = formData.mobile.replace(/\D/g, '');
      if (cleaned.length !== 10) {
        newErrors.mobile = 'Contact number must be exactly 10 digits';
      } else {
        const excludeId = member?.member_id || member?.id;
        const rel = formData.relationship || member?.relationship || member?.relation || '';
        const conflict = findMobileSubgroupDuplicate(formData.mobile, rel, existingMembers, excludeId);
        if (conflict) {
          newErrors.mobile = `This number is already used by ${conflict.name ? `${conflict.name} (${capitalizeRelationship(conflict.relationship || conflict.relation)})` : capitalizeRelationship(conflict.relationship || conflict.relation)}`;
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(member.member_id || member.id, formData);
    }
  };

  if (!mounted || !show || !member) return null;

  const currentRel = normalizeRelationshipForApi(member.relationship || member.relation || formData.relationship || '');
  const isSelfMember = currentRel === 'self';
  const selfGender = getSelfGender(existingMembers);
  const effectiveRel = isRelationshipOverride
    ? normalizeRelationshipForApi(formData.relationship)
    : currentRel;
  const genderOptions = getGenderOptions(effectiveRel, selfGender);
  const isGenderLocked = isSelfMember && !!(member.gender);
  const existingSingletonRelationsForEdit = new Set();
  if (member) {
    existingSingletonRelations.forEach(rel => {
      existingSingletonRelationsForEdit.add(rel);
    });
  }

  return createPortal(
    <div className="fixed top-0 left-0 w-screen h-screen bg-black/80 z-[9999] m-0 p-0 flex items-center justify-center">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl m-6">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit Family Member</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FaTimes />
          </button>
        </div>
        <div className="p-6 space-y-6">
          {errors.api && <div className="text-sm text-red-600">{errors.api}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
            <input
              className={`w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Relationship *</label>
            {isSelfMember ? (
              <div className="flex items-center space-x-2">
                <input
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900"
                  value="Self"
                  disabled
                />
                <FaLock className="text-gray-400" />
              </div>
            ) : !isRelationshipOverride ? (
              <div className="flex items-center space-x-2">
                <input
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-900"
                  value={capitalizeRelationship(member.relationship || member.relation || formData.relationship)}
                  disabled
                />
                <FaLock className="text-gray-400" />
                <button
                  onClick={() => {
                    const normalizedRel = normalizeRelationshipForApi(formData.relationship);
                    setFormData(prev => ({ ...prev, relationship: normalizedRel }));
                    setIsRelationshipOverride(true);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                >
                  Override
                </button>
              </div>
            ) : (
              <div>
                <select
                  className={`w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.relationship ? 'border-red-500' : 'border-gray-300'}`}
                  value={normalizeRelationshipForApi(formData.relationship)}
                  onChange={(e) => handleChange('relationship', e.target.value)}
                >
                  <option value="">Select relationship</option>
                  <option value="self" disabled={existingSingletonRelationsForEdit.has('self')}>
                    {existingSingletonRelationsForEdit.has('self') ? 'Self (Already added)' : 'Self'}
                  </option>
                  <option value="father" disabled={existingSingletonRelationsForEdit.has('father')}>
                    {existingSingletonRelationsForEdit.has('father') ? 'Father (Already added)' : 'Father'}
                  </option>
                  <option value="mother" disabled={existingSingletonRelationsForEdit.has('mother')}>
                    {existingSingletonRelationsForEdit.has('mother') ? 'Mother (Already added)' : 'Mother'}
                  </option>
                  <option value="son">Son</option>
                  <option value="spouse" disabled={existingSingletonRelationsForEdit.has('spouse')}>
                    {existingSingletonRelationsForEdit.has('spouse') ? 'Spouse (Already added)' : 'Spouse'}
                  </option>
                  <option value="daughter">Daughter</option>
                  <option value="brother">Brother</option>
                  <option value="sister">Sister</option>
                  <option value="mother_in_law" disabled={existingSingletonRelationsForEdit.has('mother_in_law')}>
                    {existingSingletonRelationsForEdit.has('mother_in_law') ? 'Mother-in-law (Already added)' : 'Mother-in-law'}
                  </option>
                  <option value="father_in_law" disabled={existingSingletonRelationsForEdit.has('father_in_law')}>
                    {existingSingletonRelationsForEdit.has('father_in_law') ? 'Father-in-law (Already added)' : 'Father-in-law'}
                  </option>
                </select>
                <div className="flex items-center justify-between mt-1">
                  <div>
                    {errors.relationship && (
                      <p className="text-xs text-red-500">{errors.relationship}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsRelationshipOverride(false);
                      const rel = member?.relationship || member?.relation || '';
                      const relationLabel = formatRelationshipForApi(rel);
                      setFormData(prev => ({ ...prev, relationship: relationLabel }));
                      setErrors(prev => ({ ...prev, relationship: '' }));
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Cancel Override
                  </button>
                </div>
              </div>
            )}
            {(() => {
              if (!isSelfMember && !isRelationshipOverride) {
                return (
                  <p className="text-xs text-gray-500 mt-1">Relationship is locked by default. Click Override to change.</p>
                );
              }
              return null;
            })()}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
              <input
                type="date"
                className={`w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.dob ? 'border-red-500' : 'border-gray-300'}`}
                value={formData.dob}
                onChange={(e) => handleChange('dob', e.target.value)}
                min="1900-01-01"
                max={today}
              />
              {errors.dob && <p className="text-xs text-red-500 mt-1">{errors.dob}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
              <input
                className="w-full border rounded-md px-3 py-2 bg-gray-100 text-gray-900 focus:ring-0 focus:border-gray-300 cursor-not-allowed"
                value={formData.age !== null && formData.age !== undefined ? formData.age : ''}
                readOnly
                disabled
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
            <select
              className={`w-full border rounded-md px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isGenderLocked ? 'bg-gray-100 cursor-not-allowed border-gray-300' : 'bg-gray-50 border-gray-300'
              } ${errors.gender ? 'border-red-500' : ''}`}
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              disabled={isGenderLocked}
            >
              <option value="">Select gender</option>
              {genderOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {errors.gender && <p className="text-xs text-red-500 mt-1">{errors.gender}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mobile *</label>
            <input
              className={`w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.mobile ? 'border-red-500' : 'border-gray-300'}`}
              value={formData.mobile}
              onKeyDown={(e) => {
                const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
                if (allowedKeys.includes(e.key)) return;
                if (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) return;
                if (/^[0-9+\-() ]$/.test(e.key)) return;
                e.preventDefault();
              }}
              onChange={(e) => {
                const filtered = e.target.value.replace(/[^0-9+\-()\s]/g, '');
                handleChange('mobile', filtered);
                const cleaned = filtered.replace(/\D/g, '');
                if (filtered.trim() === '') {
                  setErrors(prev => ({ ...prev, mobile: '' }));
                } else if (cleaned.length !== 10) {
                  setErrors(prev => ({ ...prev, mobile: 'Contact number must be exactly 10 digits' }));
                } else {
                  const excludeId = member?.member_id || member?.id;
                  const rel = formData.relationship || member?.relationship || member?.relation || '';
                  const conflict = findMobileSubgroupDuplicate(filtered, rel, existingMembers, excludeId);
                  if (conflict) {
                    setErrors(prev => ({ ...prev, mobile: `This number is already used by ${conflict.name ? `${conflict.name} (${capitalizeRelationship(conflict.relationship || conflict.relation)})` : capitalizeRelationship(conflict.relationship || conflict.relation)}` }));
                  } else {
                    setErrors(prev => ({ ...prev, mobile: '' }));
                  }
                }
              }}
            />
            {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              className={`w-full border rounded-md px-3 py-2 bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:text-white hover:bg-blue-600 hover:border-blue-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              Update Member
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditFamilyMemberModal;
